import { z } from 'zod';
import { WEBSITE_ALIGNS, WEBSITE_DEVICES, WEBSITE_ICONS, WEBSITE_SPACINGS, WEBSITE_WIDTHS } from '@bulk/types';
import { idSchema } from './common';

export const localizedText = z.record(z.string(), z.string());

/** A page, an anchor, an e-mail or phone link, or a web address; never a script or data URL. */
export const hrefSchema = z
  .string()
  .max(500)
  .refine((href) => !/^(javascript|data|vbscript|file):/i.test(Array.from(href).filter((ch) => ch.charCodeAt(0) > 32).join('')), 'Use a page path, an #anchor or a web address');

export const linkSchema = z.object({
  label: localizedText,
  href: hrefSchema,
});

const assetId = idSchema.nullable().default(null);
const heading = localizedText.default({});
const intro = localizedText.default({});
const optionalLink = linkSchema.nullable().default(null);

/**
 * Where a VIDEO block's address points, and how it is played. YouTube and Vimeo are played through
 * their own privacy-friendly players; anything else must be an https file the browser can play itself.
 */
export type VideoSource = { kind: 'youtube' | 'vimeo'; id: string; embedUrl: string } | { kind: 'file'; url: string };

export function videoSource(raw: string): VideoSource | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;
  const host = url.hostname.replace(/^www\./, '');
  const youtubeId = host === 'youtu.be' ? url.pathname.slice(1) : host === 'youtube.com' || host === 'm.youtube.com' ? (url.pathname.startsWith('/embed/') || url.pathname.startsWith('/shorts/') ? url.pathname.split('/')[2] : url.searchParams.get('v')) : null;
  if (youtubeId !== null && youtubeId !== undefined) return /^[\w-]{6,20}$/.test(youtubeId) ? { kind: 'youtube', id: youtubeId, embedUrl: `https://www.youtube-nocookie.com/embed/${youtubeId}` } : null;
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const id = url.pathname.split('/').find((part) => /^\d{5,12}$/.test(part));
    return id ? { kind: 'vimeo', id, embedUrl: `https://player.vimeo.com/video/${id}` } : null;
  }
  return /\.(mp4|webm|m4v|mov)$/i.test(url.pathname) ? { kind: 'file', url: url.toString() } : null;
}

const EMBED_HOSTS: ReadonlyArray<{ host: string; path?: RegExp }> = [
  { host: 'youtube.com', path: /^\/embed\// },
  { host: 'youtube-nocookie.com', path: /^\/embed\// },
  { host: 'player.vimeo.com', path: /^\/video\// },
  { host: 'google.com', path: /^\/maps\/embed/ },
  { host: 'instagram.com', path: /^\/(p|reel)\/[\w-]+\/embed/ },
  { host: 'facebook.com', path: /^\/plugins\// },
];

/** An EMBED block only frames players and maps from these hosts; anything else could run its own script on the gym's page. */
export function embedAllowed(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || url.username || url.password || url.port) return false;
  const host = url.hostname.replace(/^www\./, '');
  return EMBED_HOSTS.some((allowed) => allowed.host === host && (!allowed.path || allowed.path.test(url.pathname)));
}

export const EMBED_ALLOWED_HOSTS = EMBED_HOSTS.map((h) => h.host);

export const blockStyleSchema = z.object({
  scheme: z
    .string()
    .trim()
    .regex(/^[a-z0-9][a-z0-9-]{0,39}$/)
    .default('default')
    .describe("A colour scheme of the site; an unknown one renders as 'default'"),
  spacing: z.enum(WEBSITE_SPACINGS).default('md'),
  width: z.enum(WEBSITE_WIDTHS).default('normal'),
  align: z.enum(WEBSITE_ALIGNS).default('start'),
  hideOn: z.array(z.enum(WEBSITE_DEVICES)).max(2).default([]),
  anchor: z
    .string()
    .trim()
    .regex(/^[a-z0-9][a-z0-9-]{0,39}$/, 'Lowercase letters, digits and dashes only')
    .nullable()
    .default(null),
});
export type BlockStyle = z.infer<typeof blockStyleSchema>;

export const heroContentSchema = z.object({
  title: localizedText,
  subtitle: localizedText.default({}),
  eyebrow: localizedText.default({}),
  showLogo: z.boolean().default(false),
  imageAssetId: assetId,
  videoUrl: z.string().max(500).nullable().default(null),
  primaryCta: optionalLink,
  secondaryCta: optionalLink,
});

export const pageHeaderContentSchema = z.object({
  title: localizedText,
  subtitle: localizedText.default({}),
  imageAssetId: assetId,
});

export const aboutContentSchema = z.object({
  heading: localizedText,
  body: localizedText,
  imageAssetId: assetId,
  highlights: z.array(z.object({ title: localizedText, text: localizedText })).default([]),
});

export const mediaTextContentSchema = z.object({
  eyebrow: localizedText.default({}),
  heading,
  body: localizedText.default({}).describe('Markdown, sanitised when rendered'),
  imageAssetId: assetId,
  videoUrl: z
    .string()
    .max(500)
    .refine((url) => videoSource(url) !== null, 'A YouTube or Vimeo link, or an https video file')
    .nullable()
    .default(null),
  cta: optionalLink,
});

export const columnsContentSchema = z.object({
  heading,
  intro,
  columns: z.number().int().min(2).max(4).default(3),
  items: z
    .array(
      z.object({
        icon: z.enum(WEBSITE_ICONS).nullable().default(null),
        imageAssetId: assetId,
        title: localizedText.default({}),
        text: localizedText.default({}),
        link: optionalLink,
      }),
    )
    .max(12)
    .default([]),
});

export const plansContentSchema = z.object({
  heading: localizedText.default({}),
  intro: localizedText.default({}),
  planIds: z.array(idSchema).default([]),
  showAllPublic: z.boolean().default(true),
  groupByBranch: z.boolean().default(false).describe("Lists the plans under each branch's name, a plan that opens every branch under each"),
});

export const classesContentSchema = z.object({
  heading: localizedText.default({}),
  intro: localizedText.default({}),
  showSchedule: z.boolean().default(true),
});

export const trainersContentSchema = z.object({
  heading: localizedText.default({}),
  intro: localizedText.default({}),
  specialtyIds: z.array(idSchema).max(20).default([]).describe('Only trainers with one of these specialities; empty means everyone on the website'),
  limit: z.number().int().min(1).max(50).nullable().default(null),
});

export const recognitionContentSchema = z.object({
  heading: localizedText.default({}),
  intro: localizedText.default({}),
  limit: z.number().int().min(1).max(24).nullable().default(null),
});

export const facilitiesContentSchema = z.object({
  heading: localizedText.default({}),
  items: z
    .array(
      z.object({
        title: localizedText,
        text: localizedText.default({}),
        icon: z.string().max(40).nullable().default(null),
        imageAssetId: assetId,
      }),
    )
    .default([]),
});

export const statsContentSchema = z.object({
  heading,
  intro,
  items: z
    .array(z.object({ value: z.string().trim().max(24), label: localizedText.default({}) }))
    .max(8)
    .default([]),
});

export const timelineContentSchema = z.object({
  heading,
  intro,
  items: z
    .array(
      z.object({
        label: z.string().trim().max(24).default(''),
        title: localizedText.default({}),
        text: localizedText.default({}),
        imageAssetId: assetId,
      }),
    )
    .max(20)
    .default([]),
});

export const stepsContentSchema = z.object({
  heading,
  intro,
  items: z
    .array(z.object({ title: localizedText.default({}), text: localizedText.default({}) }))
    .max(8)
    .default([]),
});

export const quoteContentSchema = z.object({
  quote: localizedText,
  author: z.string().trim().max(120).default(''),
  role: localizedText.default({}),
  imageAssetId: assetId,
});

export const galleryContentSchema = z.object({
  heading: localizedText.default({}),
  imageAssetIds: z.array(idSchema).default([]),
});

export const testimonialsContentSchema = z.object({
  heading: localizedText.default({}),
  items: z
    .array(z.object({ author: z.string().max(120), quote: localizedText, rating: z.number().int().min(1).max(5).nullable().default(null) }))
    .default([]),
});

export const faqContentSchema = z.object({
  heading: localizedText.default({}),
  items: z.array(z.object({ question: localizedText, answer: localizedText })).default([]),
});

export const imageContentSchema = z.object({
  imageAssetId: assetId,
  alt: localizedText.default({}),
  caption: localizedText.default({}),
  href: hrefSchema.nullable().default(null),
});

export const videoContentSchema = z.object({
  heading,
  url: z
    .string()
    .max(500)
    .refine((url) => videoSource(url) !== null, 'A YouTube or Vimeo link, or an https video file')
    .nullable()
    .default(null),
  posterAssetId: assetId,
  caption: localizedText.default({}),
  autoplay: z.boolean().default(false).describe("Play on screen: any source (a file, YouTube or Vimeo) plays muted and looping while at least half of it is visible and pauses when it scrolls away or the tab is hidden, whatever the visitor's motion setting, and its controls stop it; only a cover's background film, which has none, stays on its poster for a visitor whose device asks for reduced motion when the design respects that"),
});

export const logosContentSchema = z.object({
  heading,
  items: z
    .array(z.object({ imageAssetId: assetId, name: z.string().trim().max(80).default(''), href: hrefSchema.nullable().default(null) }))
    .max(24)
    .default([]),
});

export const scheduleContentSchema = z.object({
  heading,
  intro,
  days: z.number().int().min(1).max(7).default(7),
});

export const offerContentSchema = z.object({
  heading,
  text: localizedText.default({}).describe('Shown when no campaign is running, if the block is set to show then'),
  cta: optionalLink,
  showWhenNone: z.boolean().default(false),
});

export const locationsContentSchema = z.object({ heading, intro });

export const hoursContentSchema = z.object({
  heading,
  note: localizedText.default({}),
});

export const contactContentSchema = z.object({
  heading: localizedText.default({}),
  address: z.string().max(300).nullable().default(null),
  phone: z.string().max(40).nullable().default(null),
  email: z.string().max(120).nullable().default(null),
  hoursText: localizedText.default({}),
  showForm: z.boolean().default(true),
});

export const mapContentSchema = z.object({
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
  embedUrl: z.string().max(1000).nullable().default(null),
});

export const socialContentSchema = z.object({
  heading,
  text: localizedText.default({}),
});

export const ctaContentSchema = z.object({
  title: localizedText,
  text: localizedText.default({}),
  cta: optionalLink,
});

export const buttonsContentSchema = z.object({
  items: z
    .array(z.object({ label: localizedText, href: hrefSchema, style: z.enum(['primary', 'outline']).default('primary') }))
    .max(4)
    .default([]),
});

export const spacerContentSchema = z.object({
  size: z.enum(['sm', 'md', 'lg', 'xl']).default('md'),
});

export const dividerContentSchema = z.object({
  style: z.enum(['line', 'dots', 'accent']).default('line'),
});

export const embedContentSchema = z.object({
  heading,
  url: z
    .string()
    .max(1000)
    .refine(embedAllowed, `Only an embed link from ${EMBED_HOSTS.map((h) => h.host).join(', ')}`)
    .nullable()
    .default(null),
  aspect: z.enum(['16:9', '4:3', '1:1', '9:16']).default('16:9'),
  title: z.string().trim().max(120).default('').describe('Read out by screen readers'),
});

/**
 * Free-form bilingual prose, for the copy that does not fit any of the marketing-shaped sections.
 * The body is markdown, rendered and sanitised by exactly the pipeline the legal pages use.
 */
export const richTextContentSchema = z.object({
  heading: localizedText.default({}),
  body: localizedText.default({}),
  width: z.enum(['narrow', 'wide']).default('narrow'),
});

/** Which of the gym's menu categories to show, and how. Empty `categoryIds` means every visible one. */
export const menuContentSchema = z.object({
  heading: localizedText.default({}),
  intro: localizedText.default({}),
  categoryIds: z.array(idSchema).default([]),
  layout: z.enum(['list', 'cards']).default('list'),
});

/** The gym's open jobs with an application form. The openings come resolved on the site payload; `privacySlug` is the content page the consent line links to. */
export const careersContentSchema = z.object({
  heading: localizedText.default({}),
  intro: localizedText.default({}),
  privacySlug: z.string().trim().min(1).max(63).default('privacy'),
});

export const sectionContentSchemas = {
  HERO: heroContentSchema,
  ABOUT: aboutContentSchema,
  PLANS: plansContentSchema,
  CLASSES: classesContentSchema,
  TRAINERS: trainersContentSchema,
  FACILITIES: facilitiesContentSchema,
  GALLERY: galleryContentSchema,
  TESTIMONIALS: testimonialsContentSchema,
  FAQ: faqContentSchema,
  CONTACT: contactContentSchema,
  MAP: mapContentSchema,
  CTA: ctaContentSchema,
  RICH_TEXT: richTextContentSchema,
  MENU: menuContentSchema,
  PAGE_HEADER: pageHeaderContentSchema,
  MEDIA_TEXT: mediaTextContentSchema,
  COLUMNS: columnsContentSchema,
  STATS: statsContentSchema,
  TIMELINE: timelineContentSchema,
  STEPS: stepsContentSchema,
  QUOTE: quoteContentSchema,
  IMAGE: imageContentSchema,
  VIDEO: videoContentSchema,
  LOGOS: logosContentSchema,
  SCHEDULE: scheduleContentSchema,
  OFFER: offerContentSchema,
  LOCATIONS: locationsContentSchema,
  HOURS: hoursContentSchema,
  SOCIAL: socialContentSchema,
  BUTTONS: buttonsContentSchema,
  SPACER: spacerContentSchema,
  DIVIDER: dividerContentSchema,
  EMBED: embedContentSchema,
  RECOGNITION: recognitionContentSchema,
  CAREERS: careersContentSchema,
} as const;

export type SectionContentType = keyof typeof sectionContentSchemas;
export type SectionContent<T extends SectionContentType> = z.output<(typeof sectionContentSchemas)[T]>;

/**
 * Every media id a block's content points at. Image fields are named `…AssetId` or `…AssetIds` by
 * convention (a test holds every block schema to it), so this finds them at any depth without a list of
 * key names that the next block would have to remember to extend.
 */
export function blockAssetIds(value: unknown, into: Set<string> = new Set()): Set<string> {
  if (Array.isArray(value)) {
    for (const item of value) blockAssetIds(item, into);
    return into;
  }
  if (value === null || typeof value !== 'object') return into;
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (key.endsWith('AssetId') && typeof child === 'string') {
      into.add(child);
    } else if (key.endsWith('AssetIds') && Array.isArray(child)) {
      for (const id of child) if (typeof id === 'string') into.add(id);
    } else if (typeof child === 'object') {
      blockAssetIds(child, into);
    }
  }
  return into;
}
